import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { HorizontalCalendarComponent } from 'src/app/shared/components/vacations/horizontal-calendar/horizontal-calendar.component';
import { PetitionsComponent } from 'src/app/shared/components/vacations/petitions/petitions.component';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import {
  OperativeResponse,
  CalendarWeek,
  Operative,
  OperativeWeekConfigsResponse,
  OperativeWeekConfig,
  SyncWeekConfigsResponse,
} from 'src/app/shared/interfaces/holidays.interface';

@Component({
  selector: 'app-operational',
  imports: [
    LoaderComponent,
    MatIconModule,
    RouterLink,
    MatButtonModule,
    MatSlideToggleModule,
    MatTabGroup,
    MatTab,
    HorizontalCalendarComponent,
    PetitionsComponent,
  ],
  templateUrl: './operational.component.html',
  styleUrls: [
    './operational.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class OperationalComponent implements OnInit {
  operative: Operative | null = null;
  calendarWeeks: CalendarWeek[] = [];
  operativeId?: number;
  year?: number;
  id?: number;
  isLoadingCalendar: boolean = false;
  refreshingWeekNumber: number | null = null;
  private pendingRequests: number = 0;
  private isInitialLoad: boolean = false;

  constructor(
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private holidaysService: HolidaysService
  ) {}

  ngOnInit(): void {
    const operativeId = this.route.snapshot.params['opId'];
    this.year = this.route.snapshot.params['year'];
    this.id = this.route.snapshot.params['id'];
    if (operativeId && this.id && this.year) {
      this.operativeId = Number(operativeId);
      this.loaderService.showLoader();
      this.isInitialLoad = true;
      this.pendingRequests = 2; // One for operative, one for week configs
      this.getOperative(this.operativeId);
      this.loadWeekConfigs();
    }
  }

  private hideLoaderIfDone(): void {
    if (this.isInitialLoad && this.pendingRequests === 0) {
      this.loaderService.hideLoader();
      this.isInitialLoad = false;
    }
  }

  getOperative(id: number) {
    this.holidaysService.getOperativeById(id, false).subscribe({
      next: (data) => {
        this.operative = data;
        console.log('Operative data:', this.operative);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
      error: (error) => {
        console.error('Error fetching operative:', error);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
    });
  }

  loadWeekConfigs() {
    if (!this.operativeId || !this.id || !this.year) {
      console.error('Missing required parameters');
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    this.isLoadingCalendar = true;
    const requestData = {
      operativa_id: this.operativeId,
      exercise_id: this.id,
      year: this.year,
    };

    this.holidaysService.getOperativeWeekConfigs(requestData).subscribe({
      next: (response: OperativeWeekConfigsResponse) => {
        if (response.count === 0 || response.results.length === 0) {
          // No weeks found, sync them
          // Keep isLoadingCalendar true, syncWeeks will handle it
          // Don't decrement pendingRequests yet - syncWeeks will handle it
          this.syncWeeks();
        } else {
          // Transform and display weeks
          this.calendarWeeks = this.transformWeekConfigsToCalendarWeeks(
            response.results
          );
          this.isLoadingCalendar = false;
          console.log('Week configs loaded:', this.calendarWeeks);
          if (this.isInitialLoad) {
            this.pendingRequests--;
            this.hideLoaderIfDone();
          }
        }
      },
      error: (error) => {
        this.isLoadingCalendar = false;
        console.error('Error fetching week configs:', error);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
    });
  }

  syncWeeks() {
    if (!this.operativeId || !this.id) {
      console.error('Missing required parameters for sync');
      this.isLoadingCalendar = false;
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    // isLoadingCalendar is already true from loadWeekConfigs
    const syncData = {
      operativa_id: this.operativeId,
      exercise_id: this.id,
    };

    this.holidaysService.syncWeekConfigs(syncData).subscribe({
      next: (response: SyncWeekConfigsResponse) => {
        console.log('Weeks synced successfully:', response);
        // After sync, reload the week configs
        // loadWeekConfigs will manage isLoadingCalendar and decrement pendingRequests
        this.loadWeekConfigs();
      },
      error: (error) => {
        this.isLoadingCalendar = false;
        console.error('Error syncing weeks:', error);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
    });
  }

  transformWeekConfigsToCalendarWeeks(
    configs: OperativeWeekConfig[]
  ): CalendarWeek[] {
    return configs.map((config) => ({
      id: config.id,
      week_number: config.week_number,
      week_name: `Week ${config.week_number}`,
      start_date: this.formatDateToDDMMYYYY(config.start_date),
      end_date: this.formatDateToDDMMYYYY(config.end_date),
      is_highlighted: false,
      has_warnings: config.camions_parats_real < 0,
      camiones_total: config.camions_assignats + config.camions_plaza,
      camiones_averiados: config.camions_indisponibles,
      camiones_disponibles: config.camions_disponibles,
      conductores_total: config.conductors_previstos,
      conductores_vacaciones: config.conductors_vacances,
      conductores_indisponibles: config.conductors_indisponibles,
      conductores_prevision: config.conductors_previstos,
      conductores_disponibles: config.conductors_disponibles,
      camiones_parados: {
        actual: config.camions_parats_real,
        objetivo: config.camions_parats_objectiu,
      },
      slots: config.slots,
    }));
  }

  formatDateToDDMMYYYY(dateString: string): string {
    // Convert YYYY-MM-DD to DD/MM/YYYY
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  onRefreshWeek(weekNumber: number): void {
    if (!this.operativeId || !this.id || !this.year) {
      console.error('Missing required parameters for refresh');
      return;
    }

    const refreshData = {
      operativa_id: this.operativeId,
      exercise_id: this.id,
      week_numbers: [weekNumber],
    };

    console.log('Refreshing week number:', refreshData);

    // Show skeleton loader for this specific week
    this.refreshingWeekNumber = weekNumber;

    this.holidaysService.refreshWeekConfig(refreshData).subscribe({
      next: (response: SyncWeekConfigsResponse) => {
        console.log('Week refreshed successfully:', response);
        // Fetch only the updated week
        this.loadSingleWeek(weekNumber);
      },
      error: (error) => {
        this.refreshingWeekNumber = null;
        console.error('Error refreshing week:', error);
      },
    });
  }

  loadSingleWeek(weekNumber: number): void {
    if (!this.operativeId || !this.id || !this.year) {
      console.error('Missing required parameters');
      this.refreshingWeekNumber = null;
      return;
    }

    const requestData = {
      operativa_id: this.operativeId,
      exercise_id: this.id,
      year: this.year,
      week_numbers: [weekNumber],
    };

    this.holidaysService.getOperativeWeekConfigs(requestData).subscribe({
      next: (response: OperativeWeekConfigsResponse) => {
        if (response.results.length > 0) {
          // Transform the updated week
          const updatedWeek = this.transformWeekConfigsToCalendarWeeks(
            response.results
          )[0];

          // Find and replace the week in the array
          const weekIndex = this.calendarWeeks.findIndex(
            (week) => week.week_number === weekNumber
          );

          if (weekIndex !== -1) {
            // Create a new array with the updated week to trigger change detection
            this.calendarWeeks = [
              ...this.calendarWeeks.slice(0, weekIndex),
              updatedWeek,
              ...this.calendarWeeks.slice(weekIndex + 1),
            ];
            console.log('Week updated successfully:', updatedWeek);
          }
        }
        this.refreshingWeekNumber = null;
      },
      error: (error) => {
        this.refreshingWeekNumber = null;
        console.error('Error fetching updated week config:', error);
      },
    });
  }

  onWeekUpdated(): void {
    // Reload week configs after a successful update
    this.loadWeekConfigs();
  }

  get isLoading(): boolean {
    return this.loaderService.getLoader();
  }
}
